import { GlobeHemisphereWest } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  isAppLocale,
  setLocale,
  type AppLocale,
} from '@/lib/i18n'

interface LanguageSwitcherProps {
  className?: string
  /**
   * Optional auth-aware handler. When omitted the choice is only persisted
   * locally (`kuniri.locale`); authenticated screens pass a handler that also
   * syncs `users.locale` (D-038).
   */
  onLocaleChange?: (locale: AppLocale) => Promise<boolean> | boolean
}

/**
 * Explicit language choice. Persists `kuniri.locale` and updates the document
 * language; the auth-aware handler adds the `PATCH /me {locale}` sync.
 */
export function LanguageSwitcher({
  className,
  onLocaleChange,
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation()
  const [syncing, setSyncing] = useState(false)
  const [syncFailed, setSyncFailed] = useState(false)
  const current: AppLocale = isAppLocale(i18n.language) ? i18n.language : 'it'

  async function change(next: string) {
    if (!isAppLocale(next)) return
    setSyncing(true)
    setSyncFailed(false)
    await setLocale(next)
    const synced = onLocaleChange ? await onLocaleChange(next) : true
    setSyncFailed(!synced)
    setSyncing(false)
  }

  return (
    <div className={className ?? 'inline-flex flex-col gap-1'}>
      <label className="inline-flex items-center gap-2">
        <GlobeHemisphereWest
          size={18}
          weight="bold"
          aria-hidden="true"
          className="text-teal"
        />
        <span className="sr-only">{t('common.language')}</span>
        <select
          className="min-h-11 rounded-sm border border-input-border bg-white px-2 py-1 text-base font-semibold text-deep disabled:opacity-60"
          value={current}
          disabled={syncing}
          onChange={(event) => void change(event.target.value)}
        >
          <option value="it">{t('common.languages.it')}</option>
          <option value="en">{t('common.languages.en')}</option>
        </select>
      </label>
      {syncFailed ? (
        <p
          role="status"
          className="basis-full text-xs font-semibold text-danger"
        >
          {t('auth.localeSyncError')}
        </p>
      ) : null}
    </div>
  )
}
